import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type Paginated, type ApiSuccess } from '../../lib/api';

interface MessageRow {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  subject: string | null;
  message: string;
  status: 'NEW' | 'READ' | 'REPLIED' | 'CLOSED';
  repliedAt: string | null;
  replier?: { id: string; name: string } | null;
  createdAt: string;
}

interface MessageStats {
  total: number;
  new: number;
  read: number;
  replied: number;
  closed: number;
}

const STATUS: Record<string, { label: string; color: 'amber' | 'navy' | 'green' | 'gray'; icon: string }> = {
  NEW:     { label: 'جديد',     color: 'amber', icon: '🆕' },
  READ:    { label: 'مقروء',    color: 'navy',  icon: '👁️' },
  REPLIED: { label: 'تم الرد',  color: 'green', icon: '✓' },
  CLOSED:  { label: 'مغلق',     color: 'gray',  icon: '🔒' },
};

export function AdminMessagesPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ q: '', status: '', page: 1 });
  const [open, setOpen] = useState<MessageRow | null>(null);

  const { data: stats } = useQuery({
    queryKey: ['admin', 'messages', 'stats'],
    queryFn: () => api.get<ApiSuccess<MessageStats>>('/api/messages/stats'),
    refetchInterval: 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'messages', filters],
    queryFn: () => api.get<Paginated<MessageRow>>('/api/messages', { query: { ...filters, limit: 30 } }),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'messages'] });
  };

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.put(`/api/messages/${id}`, { status }),
    onSuccess: invalidateAll,
  });

  const deleteMsg = useMutation({
    mutationFn: (id: string) => api.delete(`/api/messages/${id}`),
    onSuccess: () => { invalidateAll(); setOpen(null); },
  });

  const openMessage = (m: MessageRow) => {
    setOpen(m);
    if (m.status === 'NEW') updateStatus.mutate({ id: m.id, status: 'READ' });
  };

  return (
    <div>
      {/* 🎯 رأس الصفحة */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-navy-dark">📬 رسائل التواصل</h1>
          <p className="text-sm text-ink-muted mt-1">رسائل من نموذج "اتصل بنا" — يصل إيميل تلقائي للمرسل والإدارة عند كل رسالة جديدة</p>
        </div>
      </div>

      {/* 📊 شارات الإحصاءات */}
      {stats?.data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard label="الإجمالي"  value={stats.data.total}   color="navy"  icon="📨" onClick={() => setFilters({ ...filters, status: '', page: 1 })} active={!filters.status} />
          <StatCard label="جديد"      value={stats.data.new}     color="amber" icon="🆕" onClick={() => setFilters({ ...filters, status: 'NEW', page: 1 })} active={filters.status === 'NEW'} pulse />
          <StatCard label="مقروء"     value={stats.data.read}    color="navy"  icon="👁️" onClick={() => setFilters({ ...filters, status: 'READ', page: 1 })} active={filters.status === 'READ'} />
          <StatCard label="تم الرد"   value={stats.data.replied} color="green" icon="✓"  onClick={() => setFilters({ ...filters, status: 'REPLIED', page: 1 })} active={filters.status === 'REPLIED'} />
          <StatCard label="مغلق"      value={stats.data.closed}  color="gray"  icon="🔒" onClick={() => setFilters({ ...filters, status: 'CLOSED', page: 1 })} active={filters.status === 'CLOSED'} />
        </div>
      )}

      {/* 🔍 شريط البحث */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Input
                placeholder="🔍 بحث (الاسم/الإيميل/الجوال/الموضوع/الرسالة)…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })}
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              <option value="">كل الحالات</option>
              <option value="NEW">🆕 جديد فقط</option>
              <option value="READ">👁️ مقروء</option>
              <option value="REPLIED">✓ تم الرد</option>
              <option value="CLOSED">🔒 مغلق</option>
            </select>
          </div>
        </CardBody>
      </Card>

      {/* 📋 جدول الرسائل */}
      <Card>
        <CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>المرسل</Th>
                <Th>الموضوع / مقتطف</Th>
                <Th>التواصل</Th>
                <Th>التاريخ</Th>
                <Th>الحالة</Th>
                <Th>إجراءات</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-500">⟳ جاري التحميل…</td></tr>
              ) : !data?.data?.length ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-500">
                  <div className="text-5xl mb-2">📭</div>
                  لا توجد رسائل تطابق الفلاتر
                </td></tr>
              ) : data.data.map((m) => (
                <tr key={m.id} className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition ${m.status === 'NEW' ? 'bg-amber-50/30 font-bold' : ''}`} onClick={() => openMessage(m)}>
                  <Td>
                    <div className="font-bold text-navy-dark">{m.name}</div>
                    {m.email && <div className="text-[10px] text-ink-muted truncate" dir="ltr">{m.email}</div>}
                  </Td>
                  <Td>
                    <div className="font-bold text-sm line-clamp-1">{m.subject ?? '— تواصل عام —'}</div>
                    <div className="text-[11px] text-ink-muted line-clamp-1">{m.message}</div>
                  </Td>
                  <Td className="text-xs" dir="ltr">{m.phone}</Td>
                  <Td className="text-xs whitespace-nowrap">{new Date(m.createdAt).toLocaleString('ar-SA')}</Td>
                  <Td>
                    <Badge color={STATUS[m.status]?.color ?? 'gray'}>
                      {STATUS[m.status]?.icon} {STATUS[m.status]?.label ?? m.status}
                    </Badge>
                  </Td>
                  <Td>
                    <Button size="sm" variant="primary">📂 فتح</Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button size="sm" variant="outline" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>السابق</Button>
          <span className="px-4 py-2 text-sm font-bold">{filters.page} / {data.pagination.totalPages}</span>
          <Button size="sm" variant="outline" disabled={filters.page >= data.pagination.totalPages} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>التالي</Button>
        </div>
      )}

      {/* 📬 مودال تفاصيل الرسالة */}
      {open && (
        <MessageDetailModal
          message={open}
          onClose={() => setOpen(null)}
          onUpdated={invalidateAll}
          onDelete={() => deleteMsg.mutate(open.id)}
          deleting={deleteMsg.isPending}
        />
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ شارة إحصاءة ━━━━━━━━━━━━━━━━━━━━
function StatCard({ label, value, color, icon, onClick, active, pulse }: {
  label: string; value: number; color: 'amber' | 'navy' | 'green' | 'gray';
  icon: string; onClick: () => void; active?: boolean; pulse?: boolean;
}) {
  const colors = {
    amber: { active: 'border-amber-400 bg-amber-50', text: 'text-amber-700' },
    navy:  { active: 'border-navy bg-navy-soft',     text: 'text-navy-dark' },
    green: { active: 'border-green-400 bg-green-50', text: 'text-green-700' },
    gray:  { active: 'border-gray-400 bg-gray-100',  text: 'text-gray-700' },
  }[color];
  return (
    <button
      onClick={onClick}
      className={`text-right p-3 rounded-2xl border-2 bg-white transition hover:shadow-brand ${
        active ? colors.active : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-2xl">{icon}</span>
        {pulse && value > 0 && <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />}
      </div>
      <div className={`text-2xl font-black mt-1 ${colors.text}`}>{value}</div>
      <div className="text-xs text-ink-muted">{label}</div>
    </button>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ مودال تفاصيل الرسالة + الرد ━━━━━━━━━━━━━━━━━━━━
function MessageDetailModal({ message, onClose, onUpdated, onDelete, deleting }: {
  message: MessageRow;
  onClose: () => void;
  onUpdated: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const qc = useQueryClient();
  const [replyText, setReplyText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  // 📨 إرسال رد فعلي بالإيميل
  const reply = useMutation({
    mutationFn: () => api.post(`/api/messages/${message.id}/reply`, { replyText }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'messages'] });
      onUpdated();
      setReplyText('');
      onClose();
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: string) => api.put(`/api/messages/${message.id}`, { status }),
    onSuccess: onUpdated,
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        {/* 🎯 رأس */}
        <div className="bg-gradient-to-l from-navy-darker to-navy-dark text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-2xl">📬</div>
            <div className="min-w-0">
              <h2 className="font-black text-lg truncate">{message.subject ?? 'تواصل عام'}</h2>
              <p className="text-xs text-white/70">من <strong>{message.name}</strong> · {new Date(message.createdAt).toLocaleString('ar-SA')}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl shrink-0">✕</button>
        </div>

        <div className="p-6 space-y-5">
          {/* 📞 معلومات المرسل */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="الجوال" value={message.phone} dir="ltr" copyable />
            <Field label="الإيميل" value={message.email ?? '—'} dir="ltr" copyable={!!message.email} />
            <Field label="الحالة" value={`${STATUS[message.status]?.icon} ${STATUS[message.status]?.label ?? message.status}`} />
          </div>

          {/* 📝 الرسالة */}
          <div>
            <div className="text-xs font-bold text-ink-muted mb-2">📝 نص الرسالة:</div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm whitespace-pre-line leading-relaxed">
              {message.message}
            </div>
          </div>

          {/* ↩️ سجل الرد */}
          {message.repliedAt && (
            <div className="bg-success-bg border border-success/30 rounded-xl p-3 text-xs text-success-dark">
              ✓ تم الرد على هذه الرسالة في <b>{new Date(message.repliedAt).toLocaleString('ar-SA')}</b>
              {message.replier && <> بواسطة <b>{message.replier.name}</b></>}
            </div>
          )}

          {/* 📨 صندوق الرد */}
          {message.email ? (
            <div className="border-2 border-navy/20 rounded-xl p-4 bg-navy-soft/30">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-black text-navy-dark text-sm">↩️ ردّ على الرسالة (يُرسل بالإيميل فعلياً)</h3>
                <span className="text-[11px] text-ink-muted">سيُرسل إلى: <span dir="ltr" className="font-mono">{message.email}</span></span>
              </div>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={6}
                placeholder="اكتب ردّك الاحترافي هنا… سيصل المستخدم على بريده فوراً مع نص رسالته الأصلية ورقم التذكرة."
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm resize-y"
              />
              {reply.isError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg p-2 mt-2">
                  ⚠️ {(reply.error as Error)?.message ?? 'فشل إرسال الرد'}
                </div>
              )}
              {reply.isSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 text-xs rounded-lg p-2 mt-2">
                  ✓ تم إرسال الرد بنجاح للإيميل المسجّل
                </div>
              )}
              <div className="flex justify-between items-center mt-3 flex-wrap gap-2">
                <span className="text-[11px] text-ink-muted">{replyText.length} حرف</span>
                <Button
                  variant="primary"
                  onClick={() => reply.mutate()}
                  loading={reply.isPending}
                  disabled={replyText.trim().length < 5}
                >
                  📨 أرسل الرد بالإيميل
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
              ⚠️ لا يوجد إيميل مسجّل للمرسل — لا يمكن الرد إلكترونياً. تواصل عبر الجوال: <span dir="ltr" className="font-mono">{message.phone}</span>
            </div>
          )}

          {/* 🎛️ تحكم الحالة */}
          <div className="border-t border-gray-200 pt-4">
            <div className="text-xs font-bold text-ink-muted mb-2">🎛️ تغيير الحالة يدوياً:</div>
            <div className="flex flex-wrap gap-2">
              {(['NEW', 'READ', 'REPLIED', 'CLOSED'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus.mutate(s)}
                  disabled={message.status === s || setStatus.isPending}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition ${
                    message.status === s
                      ? 'bg-navy-dark text-white border-navy-dark cursor-not-allowed'
                      : 'bg-white border-gray-200 hover:border-navy text-ink-secondary hover:text-navy-dark'
                  }`}
                >
                  {STATUS[s].icon} {STATUS[s].label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 🚪 أسفل المودال — أزرار الإجراءات */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between flex-wrap gap-2 bg-gray-50 rounded-b-2xl">
          {confirmDelete ? (
            <div className="flex gap-2 items-center">
              <span className="text-xs text-red-700 font-bold">متأكد من الحذف نهائياً؟</span>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>إلغاء</Button>
              <Button
                size="sm"
                variant="primary"
                className="!bg-red-600 hover:!bg-red-700"
                onClick={onDelete}
                loading={deleting}
              >
                🗑️ نعم احذف
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)} className="!text-red-600">
              🗑️ حذف الرسالة
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>إغلاق</Button>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Helpers ━━━━━━━━━━━━━━━━━━━━
function Field({ label, value, dir, copyable }: { label: string; value: string; dir?: string; copyable?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!copyable) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
      <div className="text-[10px] text-ink-muted mb-1">{label}</div>
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold text-sm truncate" dir={dir}>{value}</span>
        {copyable && (
          <button
            onClick={copy}
            className="text-[10px] px-2 py-0.5 rounded bg-white border border-gray-200 hover:bg-navy-soft hover:border-navy text-ink-muted hover:text-navy-dark shrink-0"
            title="نسخ"
          >
            {copied ? '✓' : '📋'}
          </button>
        )}
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase whitespace-nowrap">{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}
